import React, { useEffect, useRef, useState } from "react";
import AddCustomer from "../customer/AddCustomer";
import EditCutomer from "../customer/EditCustomer";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import { usePermission } from "../../../context/PermissionContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import Messages from "../../../constants/Messages";
import { useAuth } from "../../../context/AuthProvider";
import { CircularProgress } from "@mui/material";
import { AddIcon, EditIcon, MinusIcon, XIcon } from "../../../constants/Icons";

const AddVoucher = ({ onClose, onAdd, isBtnLoading, isActive }) => {
  const customerSuggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const amountRef = useRef(null);
  const taxpercentageRef = useRef(null);
  const { updateSettings } = useAuth();
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCustomerData, setCustomerData] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isCustomerModalOpen, setCustomerModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditData, setEditData] = useState(null);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { customerGroups, blockedPermissions, permission } = usePermission();
  const [addGroupload, setAddGroupLoad] = useState(false);
  const [addNewGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState(null);
  const [groupName, setGroupName] = useState(customerGroups);
  const suggestionRefs = useRef([]);
  const [isData, setIsData] = useState({
    title: "",
    description: "",
    amount: "",
    taxpercentage: 0,
    trackMode: "",
  });

  console.log(selectedCustomer);
  const handleClose = () => {
    onClose();
  };

  const handleCustomerSearch = (term) => {
    setCustomerSearchTerm(term);
    if (term.trim() === "") {
      setCustomerSuggestions([]);
      setCustomerData(null);
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        setIsCustomerLoading(true);
        if (!checkNetwork()) return;
        controllerRef.current = new AbortController();
        const response = await AxiosInstance.get(
          `module/customer/search?searchtxt=${encodeURIComponent(term.trim())}`,
          { signal: controllerRef.current.signal },
        );
        setCustomerSuggestions(response.data.results || []);
        const data = { phone: "", name: "" };
        if (/^\d+$/.test(term.trim())) {
          data.phone = term.trim();
        } else {
          data.name = term.trim();
        }
        setCustomerData(data);
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          handleError(error, showAlert);
        }
      } finally {
        setIsCustomerLoading(false);
      }
    }, 200);
  };

  const handleSelectCustomer = async (customer) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/customer/details/${customer._id}`,
      );
      const fullCustomer = response.data.results;
      setSelectedCustomer(fullCustomer);
      setCustomerSearchTerm("");
      setCustomerSuggestions([]);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleCustomerModalOpen = () => {
    setSelectedCustomer(null);
    setCustomerModalOpen(true);
  };

  const handleCustomerModalClose = () => {
    setCustomerModalOpen(false);
    setCustomerData(null);
  };

  const handleResetCustomer = () => {
    setCustomerModalOpen(false);
    setCustomerData(null);
  };

  const handleNewGroupClose = () => {
    setNewGroupOpen(false);
  };

  const openEditModal = (cItem) => {
    if (
      blockedPermissions("CUSTOMER:viewDetails") ||
      permission === undefined
    ) {
      setEditData(cItem);
      setEditModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const closeEditModal = () => {
    setEditData(null);
    setEditModalOpen(false);
  };

  const validateForm = () => {
    const errs = {};
    if (!selectedCustomer) {
      customerSuggestionsRef.current.focus();
      errs.customer = "customer cannot be empty";
    } else if (isData.title.trim() === "") {
      titleRef.current.focus();
      errs.title = "Title cannot be empty";
    } else if (isData?.amount.trim() === "") {
      errs.amount = "Amount cannot be empty";
    } else if (isData.description.length > 500) {
      errs.description = "description should not exceed 500";
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstError = Object.keys(errs)[0];
      switch (firstError) {
        case "customer":
          customerSuggestionsRef.current?.focus();
          break;
        case "title":
          titleRef.current?.focus();
          break;
        case "amount":
          amountRef.current?.focus();
          break;
        case "description":
          descriptionRef.current?.focus();
          break;
        default:
          break;
      }

      return false;
    }
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let inputValue = value;

    if (name === "amount") {
      if (!/^\d*\.?\d{0,2}$/.test(value)) return;
      inputValue = value;
    }

    if (name === "descrption") {
      if (value.length > 500) return;
    }

    if (name === "taxpercentage") {
      const allowedValues = [0, 5, 12, 18, 24];
      const parsedValue = parseInt(value);
      if (isNaN(parsedValue) || !allowedValues.includes(parsedValue)) {
        inputValue = 0;
      } else {
        inputValue = parsedValue;
      }
    }

    if (name === "trackMode") {
      const allowedValues = ["paylater"];
      if (!allowedValues.includes(value)) {
        inputValue = "";
      }
    }

    const updatedData = {
      ...isData,
      [name]: inputValue,
    };
    setIsData(updatedData);

    // setErrors((prevErrors) => ({
    //   ...prevErrors,
    //   [name]: validateField(name, inputValue),
    // }));
  };

  const handleAddVoucher = (e, amountMode) => {
    e.preventDefault();
    if (!validateForm()) return;
    const addData = {
      ...isData,
      ...selectedCustomer,
      amountMode: amountMode,
    };
    console.log(addData);
    onAdd(addData);
  };

  const handleUpdte = async (updatedData) => {
    setLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        ...updatedData,
        shippingAddress: {
          addressline1: updatedData.shippingaddressline1,
          addressline2: updatedData.shippingaddressline2,
          district: updatedData.shippingdistrict,
          city: updatedData.shippingcity,
          state: updatedData.shippingstate,
          statecode: updatedData.shippingstatecode,
          country: updatedData.shippingcountry,
          pincode: updatedData.shippingpincode,
        },

        // ✅ Contact Person Object
        ...(updatedData.contactPersonname?.trim() ||
        updatedData.contactPersonNumber?.trim()
          ? {
              contactPerson: {
                ...(updatedData.contactPersonname?.trim() && {
                  name: updatedData.contactPersonname,
                }),
                ...(updatedData.contactPersonNumber?.trim() && {
                  number: updatedData.contactPersonNumber,
                }),
              },
            }
          : {}),
        customerid: updatedData._id,
      };
      const response = await AxiosInstance.patch(
        `module/customer/${updatedData._id}`,
        data,
      );

      showAlert("success", Messages.SUCCESS.CUSTOMER_UPDATED);
      setCustomerData(response.data.results);
      setSelectedCustomer((prev) => ({
        ...prev,
        customerData: response.data.results,
      }));
      closeEditModal();
      return true;
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleEnterFocus = (e, fieldName, nextRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (fieldName === "title") {
        if (!validateForm) return;
        nextRef?.current?.focus();
        return;
      }
      if (fieldName === "amount") {
        if (!validateForm) return;

        nextRef?.current?.focus();
        return;
      }
    }
  };

  const handleCustomerKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, customerSuggestions.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();

      const trimmedSearch = customerSearchTerm.trim();

      if (trimmedSearch === "") {
        titleRef.current?.focus();
        return;
      }

      const noSelection = highlightedIndex === -1;
      const hasExactMatch = customerSuggestions.some((customer) => {
        const nameMatch =
          customer.name.toLowerCase() === trimmedSearch.toLowerCase();
        const phoneMatch = customer.phone === trimmedSearch;
        return nameMatch || phoneMatch;
      });

      if (noSelection && !hasExactMatch) {
        handleCustomerModalOpen();
        return;
      }

      if (highlightedIndex >= 0) {
        const selected = customerSuggestions[highlightedIndex];
        handleSelectCustomer(selected);
        customerSuggestionsRef.current?.focus();
        setShowCustomerDropdown(false);
        setCustomerSearchTerm("");
        setHighlightedIndex(-1);
      }
    }
  };

  const handleOutsideClick = (e) => {
    if (
      customerSuggestionsRef.current &&
      !customerSuggestionsRef.current.contains(e.target)
    ) {
      setCustomerSuggestions([]);
      setCustomerSearchTerm("");
      setCustomerData("");
    }
  };

  const handleCustomerAdd = async (newData) => {
    setLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        ...newData,
        shippingAddress: {
          addressline1: newData.shippingaddressline1,
          addressline2: newData.shippingaddressline2,
          district: newData.shippingdistrict,
          city: newData.shippingcity,
          state: newData.shippingstate,
          statecode: newData.shippingstatecode,
          country: newData.shippingcountry,
          pincode: newData.shippingpincode,
        },

        ...(newData.contactPersonname?.trim() ||
        newData.contactPersonNumber?.trim()
          ? {
              contactPerson: {
                ...(newData.contactPersonname?.trim() && {
                  name: newData.contactPersonname,
                }),
                ...(newData.contactPersonNumber?.trim() && {
                  number: newData.contactPersonNumber,
                }),
              },
            }
          : {}),
      };
      const response = await AxiosInstance.post(`module/customer`, data);
      showAlert("success", Messages.SUCCESS.CUSTOMER_ADDED);
      handleResetCustomer();
      handleSelectCustomer(response.data.results);
      return true;
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerGroupAdd = async (groupName, method) => {
    setAddGroupLoad(true);
    try {
      const data = {
        name: groupName,
      };
      const response = await AxiosInstance.post("module/customer/group", data);
      const results = response.data.results;
      //  setGroupName(results);
      setGroupName((prev) => [
        { ...results },
        ...(Array.isArray(prev) ? prev : []),
      ]);
      updateSettings({
        customerGroups: [
          { ...results },
          ...(Array.isArray(customerGroups) ? customerGroups : []),
        ],
      });
      showAlert("success", "Customer Group Added Successfull");
      setAddGroupLoad(false);
      handleNewGroupClose();
      setNewGroupName(results);
      if (method === "ADD") {
        handleCustomerModalOpen();
      } else {
        openEditModal(isEditData);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setAddGroupLoad(false);
    }
  };

  useEffect(() => {
    if (customerSuggestionsRef.current) {
      customerSuggestionsRef.current.focus();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const stopEventPropagation = (e) => e.stopPropagation();
  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show d-block"
        id="StoreModal"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
          onClick={stopEventPropagation}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Add Voucher</h6>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body bg-light">
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-lg-12">
                      <div className="ms-auto position-relative">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="search"
                            autoComplete="off"
                            ref={customerSuggestionsRef}
                            value={customerSearchTerm}
                            autoFocus
                            onChange={(e) => {
                              handleCustomerSearch(e.target.value);
                              setShowCustomerDropdown(true);
                              setHighlightedIndex(-1);
                            }}
                            onKeyDown={handleCustomerKeyDown}
                            onFocus={() => {
                              if (customerSuggestions.length > 0) {
                                setShowCustomerDropdown(true);
                              }
                            }}
                            placeholder="Search customer by name, phone, email, referenceID..."
                          />
                          <button
                            className={`input-group-text ${
                              customerSearchTerm.trim() ? "bg-label-prime" : ""
                            }`}
                            onClick={
                              customerSearchTerm.trim()
                                ? handleCustomerModalOpen
                                : null
                            }
                          >
                            <i
                              className={`bx ${
                                customerSearchTerm.trim()
                                  ? "bx-plus"
                                  : "bx-search"
                              }`}
                            ></i>
                          </button>
                        </div>
                        {customerSearchTerm &&
                          customerSuggestions.length === 0 && (
                            <ul className="suggestions-list">
                              <li className="suggestion-item text-muted">
                                No customers data was found
                              </li>
                            </ul>
                          )}
                        {showCustomerDropdown &&
                          customerSuggestions.length > 0 && (
                            <ul className="suggestions-list">
                              {customerSuggestions.map((customer, index) => (
                                <li
                                  ref={(el) =>
                                    (suggestionRefs.current[index] = el)
                                  }
                                  key={customer._id}
                                  onClick={() => handleSelectCustomer(customer)}
                                  className={`suggestion-item ${
                                    highlightedIndex === index
                                      ? "highlighted"
                                      : ""
                                  }`}
                                >
                                  {customer.name} -{" "}
                                  <span className="badge badge-primary badge bg-label-blue rounded-pill">
                                    {customer.phone}
                                  </span>
                                  {customer.referenceid && (
                                    <>
                                      <small>-</small>{" "}
                                      <span className="badge badge-info badge bg-label-secondary rounded-pill">
                                        {customer.referenceid}
                                      </span>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                      <div className="col-lg-12 mt-1">
                        <div className="card shadow-none border-blue mb-0">
                          <div className="card-header bg-light-blue p-2 d-flex justify-content-between align-items-center ">
                            <div className="mb-0">Customer Info</div>
                            {selectedCustomer?.customerData && (
                              <div>
                                <button
                                  type="button"
                                  className="btn-label-primary rounded  shadow-none border-0"
                                  onClick={() =>
                                    openEditModal(selectedCustomer.customerData)
                                  }
                                >
                                  <EditIcon/>
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="card-body px-2 py-0">
                            {selectedCustomer ? (
                              <>
                                <p className="my-1 fs-13">
                                  Name :{" "}
                                  <span className="fw-600 fs-14">
                                    {selectedCustomer.customerData.name}
                                  </span>
                                </p>
                                <p className="my-1 fs-13">
                                  Phone :{" "}
                                  <span className="fw-600 fs-14">
                                    {selectedCustomer.customerData.phone}{" "}
                                  </span>
                                </p>
                                {selectedCustomer.referenceid && (
                                  <p className="my-1 fs-13">
                                    Reference ID :{" "}
                                    <span className="fw-600 fs-14">
                                      {
                                        selectedCustomer.customerData
                                          .referenceid
                                      }
                                    </span>
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="my-2 text-muted">
                                No customers has been selected
                              </p>
                            )}
                          </div>
                          {errors.customer && (
                            <div className="error-message invalid-feedback">
                              {errors.customer}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <label htmlFor="title" className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        autoComplete="off"
                        ref={titleRef}
                        value={isData?.title}
                        onChange={handleChange}
                        onKeyDown={(e) =>
                          handleEnterFocus(e, "title", descriptionRef)
                        }
                      />
                      {errors.title && (
                        <div className="invalid-feedback">{errors.title}</div>
                      )}
                    </div>
                    <div className="col-lg-12">
                      <label htmlFor="description" className="form-label">
                        Description
                      </label>
                      <textarea
                        type="text"
                        className="form-control"
                        name="description"
                        autoComplete="off"
                        ref={descriptionRef}
                        value={isData?.description}
                        onChange={handleChange}
                        rows={3}
                      ></textarea>
                      {errors.description && (
                        <div className="invalid-feedback">
                          {errors.description}
                        </div>
                      )}
                    </div>
                    <div className="col-lg-6">
                      <label htmlFor="amount" className="form-label">
                        Amount <span className="text-danger">*</span>
                      </label>

                      <div className="input-container position-relative">
                        <input
                          type="text"
                          name="amount"
                          autoComplete="off"
                          className="form-control"
                          ref={amountRef}
                          value={isData?.amount}
                          onChange={handleChange}
                          onKeyDown={(e) =>
                            handleEnterFocus(e, "amount", taxpercentageRef)
                          }
                        />
                      </div>
                      {errors.amount && (
                        <div className="invalid-feedback">{errors.amount}</div>
                      )}
                    </div>
                    <div className="col-lg-6">
                      <label htmlFor="taxpercentage" className="form-label">
                        Tax(%) <span className="text-danger">*</span>
                      </label>
                      <select
                        name="taxpercentage"
                        className="form-select"
                        ref={taxpercentageRef}
                        value={isData.taxpercentage}
                        onChange={handleChange}
                        onKeyDown={(e) =>
                          handleEnterFocus(e, "taxpercentage", null, true)
                        }
                      >
                        <option value="0">0</option>
                        <option value="5">5</option>
                        <option value="12">12</option>
                        <option value="18">18</option>
                        <option value="24">24</option>
                      </select>
                    </div>
                    {/* <div className="col-lg-6">
                      <label htmlFor="trackMode" className="form-label">
                        trackMode
                      </label>
                      <select
                        name="trackMode"
                        className="form-select"
                        ref={taxpercentageRef}
                        value={isData?.trackMode}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        <option value="paylater">Paylater</option>
                      </select>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn btn-outline-secondary text-left"
                onClick={handleClose}
              >
                <XIcon/> Cancel
              </button>

              {isBtnLoading ? (
                <button className="btn btn-primary pb-0 text-right" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  //   ref={buttonRef}
                  type="button"
                  className="btn btn-success text-right"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddVoucher(e, "CREDIT");
                  }}
                >
                  <AddIcon/> Credit
                </button>
              )}
              <button
                // ref={buttonRef}
                type="button"
                className="btn btn-danger text-right"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddVoucher(e, "DEBIT");
                }}
              >
                <MinusIcon/> Debit
              </button>
            </div>
          </div>
        </div>
      </div>
      <AddCustomer
        isActive={isActive}
        isOpen={isCustomerModalOpen}
        onClose={handleCustomerModalClose}
        onData={isCustomerData}
        onAdd={handleCustomerAdd}
        isBtnLoading={isBtnLoading}
        groupName={groupName}
        onNewGroupsIn={handleCustomerGroupAdd}
        newGroup={newGroupName}
        resetAll={true}
      />

      {isEditModalOpen && (
        <EditCutomer
          isActive={isActive}
          editData={isEditData}
          onClose={closeEditModal}
          onEdit={handleUpdte}
          isBtnLoading={isBtnLoading}
          groupName={groupName}
          groupId={selectedCustomer.customerData?.groupid}
          onNewGroupsIn={handleCustomerGroupAdd}
          resetAll={true}
          newGroup={newGroupName}
        />
      )}
    </div>
  );
};

export default AddVoucher;
